import {
  type GetProjectQueryData,
  useGetProjectVersionQuery,
  type ProjectFileType,
} from '@/generated/graphql';

type ProjectDetails = GetProjectQueryData['project'];

/**
 * Hook to encapsulate project version query logic
 * Reduces prop drilling by providing version data in a reusable hook
 * @param project - Project object (must have _id and versionHistory)
 * @returns Object with latestVersionFiles, refetchLatestVersion, loading, and error
 */
export function useProjectVersionData(
  project:
    | ProjectDetails
    | null
    | undefined
    | {
        _id?: string | null;
        versionHistory?: Array<{ version?: number }> | null;
      }
    | null
) {
  const latestVersion = Math.max(
    0,
    ...(project?.versionHistory?.map((v) => v.version || 0) || [])
  );

  const {
    data: latestVersionData,
    loading: latestVersionLoading,
    error: latestVersionError,
    refetch: refetchLatestVersion,
  } = useGetProjectVersionQuery({
    variables: {
      projectId: project?._id || '',
      version: latestVersion,
    },
    fetchPolicy: 'cache-first',
    skip: !project || !project.versionHistory?.length,
  });

  const latestVersionFiles: ProjectFileType[] =
    latestVersionData?.projectVersion?.files || [];

  return {
    latestVersionFiles,
    refetchLatestVersion,
    loading: latestVersionLoading,
    error: latestVersionError,
  };
}
